import { io, Socket } from 'socket.io-client';
import { api } from './api';
import { useNova } from './store';
import type { ChatView, DigitalOps, EmailView, NovaEvent, Snapshot } from './types';

let socket: Socket | null = null;
let onRevoked: ((message: string) => void) | undefined;

/** Called when the session stops being valid: deactivated by an admin, or expired. */
export function whenRevoked(fn: (message: string) => void): void {
  onRevoked = fn;
}
let resyncing = false;

/** Re-reads everything from the server; used on connect and after a sequence gap. */
export async function resync(): Promise<void> {
  if (resyncing) return;
  resyncing = true;
  try {
    const [snap, chats, emails] = await Promise.all([
      api<Snapshot>('GET', '/snapshot'),
      api<ChatView[]>('GET', '/chats').catch(() => [] as ChatView[]),
      api<EmailView[]>('GET', '/emails').catch(() => [] as EmailView[]),
    ]);
    useNova.getState().applySnapshot(snap);
    useNova.getState().set({
      chats: Object.fromEntries(chats.filter(Boolean).map((c) => [c.interactionId, c])),
      emails: Object.fromEntries(emails.filter(Boolean).map((m) => [m.interactionId, m])),
    });
    await loadDigital();
  } finally {
    resyncing = false;
  }
}

const SUPERVISORS = ['supervisor', 'admin', 'manager', 'platform_admin'];

/** Supervisors: every chat and email waiting or being worked on, and today's totals; events keep it live. */
export async function loadDigital(): Promise<void> {
  const role = useNova.getState().user?.role;
  if (!role || !SUPERVISORS.includes(role)) return;
  type Raw = Omit<DigitalOps, 'chats' | 'emails'> & { chats: ChatView[]; emails: EmailView[] };
  const d = await api<Raw>('GET', '/ops/digital').catch(() => null);
  if (!d) return;
  useNova.getState().set({
    digital: {
      ...d,
      chats: Object.fromEntries(d.chats.map((c) => [c.interactionId, c])),
      emails: Object.fromEntries(d.emails.map((m) => [m.interactionId, m])),
    },
  });
}

export function connectRealtime(token: string): void {
  disconnectRealtime();
  const store = useNova.getState();
  store.set({ link: 'connecting' });
  socket = io({ path: '/socket.io', auth: { token }, transports: ['websocket'], reconnectionDelayMax: 5000 });

  socket.on('ready', () => {
    useNova.getState().set({ link: 'live' });
    void resync();
  });
  socket.on('disconnect', () => useNova.getState().set({ link: 'reconnecting' }));
  socket.io.on('reconnect_attempt', () => useNova.getState().set({ link: 'reconnecting' }));
  socket.on('auth_error', (e?: { message?: string }) => onRevoked?.(e?.message ?? 'Your session ended. Sign in again.'));
  socket.on('event', (e: NovaEvent) => {
    const me = useNova.getState().user?.sub;
    if (e.type === 'agent.state_changed' && e.data.userId === me && (e.data.reason === 'deactivated' || e.data.reason === 'signed_out')) {
      onRevoked?.(e.data.reason === 'deactivated' ? 'Your account was deactivated by an admin' : 'A supervisor signed you out. Sign in again.');
      return;
    }
    const result = useNova.getState().applyEvent(e);
    if (result === 'gap') void resync();
  });
}

export function disconnectRealtime(): void {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
}
